import type { ReactNode } from 'react'
import clsx from 'clsx'
import { CheckCircle, AlertErrorCircle, WarningTriangle, XCircle, Retry } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { IconButton } from '@/components/icon-button/IconButton'
import { UploadCardStatus } from '@/components/upload-card/constants'
import { UploadCardFileIcon } from '@/components/upload-card/UploadCardFileIcon'

export interface UploadCardProps {
  /** Current status of the upload */
  status: UploadCardStatus
  /** Display filename (e.g. "Document Name.PDF") */
  filename: string
  /** Status message for Complete / Error / Warning. For Complete, defaults to "Completed" when omitted. For Error and Warning, pass a string or rich content (e.g. inline link). */
  message?: string | ReactNode
  /** Called when user clicks Cancel (Uploading only) */
  onCancel?: () => void
  /** When provided, shows dismiss (X) for Processing / Complete / Error / Warning. Called when user clicks it. */
  onDismiss?: () => void
  /** When provided and status is Error, shows Retry button. Called when user clicks it. */
  onRetry?: () => void
}

const defaultCompleteMessage = 'Completed'

export function UploadCard({ status, filename, message, onCancel, onDismiss, onRetry }: UploadCardProps) {
  const isComplete = status === UploadCardStatus.Complete
  const isError = status === UploadCardStatus.Error
  const isPending = status === UploadCardStatus.Pending
  const isUploadPending = status === UploadCardStatus.UploadPending
  const isWarning = status === UploadCardStatus.Warning
  const isUploading = status === UploadCardStatus.Uploading
  const isProcessing = status === UploadCardStatus.Processing
  const isLoading = isUploading || isPending || isUploadPending || isProcessing
  const showsDismiss = isProcessing || isComplete || isError || isWarning

  const statusMessage = message ?? (isComplete ? defaultCompleteMessage : undefined)

  return (
    <div
      className={clsx(
        'flex gap-2.5 h-[60px] items-center rounded-[5px] w-full min-w-0 max-w-full pr-3',
        'border border-brand-neutral-200 border-solid',
        isWarning && 'bg-yellow-50',
        !isWarning && 'bg-white',
        !isLoading && 'hover:bg-brand-neutral-100',
        'transition-colors'
      )}
    >
      <div className={clsx('border-r border-solid border-brand-neutral-200', 'flex h-full items-center justify-center px-3 rounded-l-[5px] shrink-0 w-[60px]')}>
        {isLoading ? <CircularProgressContinuousSized size={24} thickness={5} showTrack /> : <UploadCardFileIcon filename={filename} error={isError} />}
      </div>

      <div className="flex flex-1 flex-col gap-1 items-start justify-center min-w-0">
        <p className="min-w-0 max-w-full text-sm font-medium leading-normal text-brand-neutral-950 truncate">{filename}</p>
        <div className="flex min-w-0 w-full items-center gap-1">
          {isUploading && <span className="min-w-0 flex-1 truncate text-xs text-brand-neutral-600 italic">Uploading...</span>}

          {isUploadPending && <span className="min-w-0 flex-1 truncate text-xs text-brand-neutral-600 italic">Waiting to upload...</span>}

          {isPending && <span className="min-w-0 flex-1 truncate text-xs text-brand-neutral-600 italic">Pending...</span>}

          {isProcessing && <span className="min-w-0 flex-1 truncate text-xs text-brand-neutral-600 italic">Processing...</span>}

          {isComplete && (
            <>
              <CheckCircle className="w-3.5 h-3.5 shrink-0 text-green-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-green-800">{statusMessage}</span>
            </>
          )}

          {isError && (
            <>
              <AlertErrorCircle className="w-3.5 h-3.5 shrink-0 text-red-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-red-800">{statusMessage}</span>
            </>
          )}

          {isWarning && (
            <>
              <WarningTriangle className="w-3.5 h-3.5 shrink-0 text-yellow-800" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-xs text-yellow-800">{statusMessage}</span>
            </>
          )}
        </div>
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {onCancel && (
          <Button variant="plain" color="destructive" size="xs" className="shrink-0" onClick={onCancel}>
            Cancel
          </Button>
        )}

        {isError && onRetry && (
          <Button variant="plain" color="secondary" size="xs" leadingIcon={<Retry className="w-3.5 h-3.5 shrink-0" />} onClick={onRetry}>
            Retry
          </Button>
        )}

        {showsDismiss && onDismiss && (
          <IconButton variant="plain" color="neutral" size="md" icon={<XCircle className="w-5 h-5" />} aria-label="Dismiss" onClick={onDismiss} />
        )}
      </div>
    </div>
  )
}
